import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useIdleLogout } from "./hooks/useIdleLogout";
import { IdleWarningModal } from "./components/IdleWarningModal";
import { ForceCredentialChange } from "./components/ForceCredentialChange";

// Pages
import { Landing } from "./pages/Landing";
import PublicLanding from "./pages/PublicLanding";
import { BookingFlow } from "./pages/BookingFlow";
import { PartyBuilder } from "./pages/PartyBuilder";
import { AllergyForm } from "./pages/AllergyForm";
import { Payment } from "./pages/Payment";
import { BookingSuccess } from "./pages/BookingSuccess";
import { BookingHeld } from "./pages/BookingHeld";
import WaiverForm from "./pages/WaiverForm";import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ResetPassword } from "./pages/ResetPassword";
import { AuthCallback } from "./pages/AuthCallback";
import { AdminDashboard } from "./pages/AdminDashboard";
import { AdminRunSheet } from "./pages/AdminRunSheet";
import { MyBookings } from "./pages/MyBookings";

// CRM Pages
import { CrmLayout } from "./pages/crm/CrmLayout";
import { CrmOverview } from "./pages/crm/CrmOverview";
import { CrmCustomers } from "./pages/crm/CrmCustomers";
import CrmBookingDetail from "./pages/crm/CrmBookingDetail";
import { CrmInvoices } from "./pages/crm/CrmInvoices";
import { CrmPayments } from "./pages/crm/CrmPayments";
import { CrmCommunications } from "./pages/crm/CrmCommunications";
import { CrmAnalytics } from "./pages/crm/CrmAnalytics";
import { CrmDiscounts } from "./pages/crm/CrmDiscounts";
import { CrmHeroTestimonials } from "./pages/crm/CrmHeroTestimonials";
import { CrmLoyalty } from "./pages/crm/CrmLoyalty";
import { CrmInvitations } from "./pages/crm/CrmInvitations";
import { CrmEmails } from "./pages/crm/CrmEmails";
import { CrmInventory } from "./pages/crm/CrmInventory";
import { CrmAccounting } from "./pages/crm/CrmAccounting";
import { CrmPayroll } from "./pages/crm/CrmPayroll";
import { CrmAddons } from "./pages/crm/CrmAddons";
import { CrmBankImports } from "./pages/crm/CrmBankImports";
import { CrmBankImportClassify } from "./pages/crm/CrmBankImportClassify";
import { CrmInvitationDetail } from "./pages/crm/CrmInvitationDetail";
import { MyLoyalty } from "./pages/MyLoyalty";
import MyInvites from "./pages/MyInvites";
import InviteCreate from "./pages/InviteCreate";
import InviteDetail from "./pages/InviteDetail";
import Rsvp from "./pages/Rsvp";
import LoyaltyInfo from "./pages/LoyaltyInfo";
import InvitesInfo from "./pages/InvitesInfo";
import { CrmEmployees } from "./pages/crm/CrmEmployees";
import { CrmStaffPerformance } from "./pages/crm/CrmStaffPerformance";
import { CrmShifts } from "./pages/crm/CrmShifts";
import { CrmShiftSwaps } from "./pages/crm/CrmShiftSwaps";
import { CrmReactivationRequests } from "./pages/crm/CrmReactivationRequests";
import CrmWaivers from "./pages/crm/CrmWaivers";
import { CrmEmployerSettings } from "./pages/crm/CrmEmployerSettings";
import { CrmEmployeeDetail } from "./pages/crm/CrmEmployeeDetail";
import { CrmSupport } from "./pages/crm/CrmSupport";
import { CrmMessages } from "./pages/crm/CrmMessages";
import { CrmCustomerMessages } from "./pages/crm/CrmCustomerMessages";
import { CrmGuide } from "./pages/crm/CrmGuide";
import { CrmReminders } from "./pages/crm/CrmReminders";
import { CrmSettings } from "./pages/crm/CrmSettings";
import { CrmPackages } from "./pages/crm/CrmPackages";
import { CrmTimeSlots } from "./pages/crm/CrmTimeSlots";
import { CrmSpecialOffers } from "./pages/crm/CrmSpecialOffers";
import { CrmCmsLanding } from "./pages/crm/CrmCmsLanding";
import { CrmLandingDesigns } from "./pages/crm/CrmLandingDesigns";
import { CrmMenuManager } from "./pages/crm/CrmMenuManager";
import { CrmContactSettings } from "./pages/crm/CrmContactSettings";
import CrmAdminControl from "./pages/crm/CrmAdminControl";
import { CrmAudit } from "./pages/crm/CrmAudit";
import { CrmProspects } from "./pages/crm/CrmProspects";
import { CrmJobApplications } from "./pages/crm/CrmJobApplications";
import CrmGiftCards from "./pages/crm/CrmGiftCards";
import PosRedeem from "./pages/crm/PosRedeem";
import GiftCardPurchase from "./pages/GiftCardPurchase";
import GiftCardSuccess from "./pages/GiftCardSuccess";
import MyGiftCards from "./pages/MyGiftCards";
import { MyMessages } from "./pages/MyMessages";

// Public invoice portal
import { InvoiceView } from "./pages/InvoiceView";
import { Contact } from "./pages/Contact";
import { Careers } from "./pages/Careers";
import { ExperienceDetailTemplate } from "./pages/experiences/ExperienceDetailTemplate";

// Global components
import { ChatWidget } from "./components/ChatWidget";

// Employee
import { EmployeeDashboard } from "./pages/EmployeeDashboard";

// Redirect legacy /admin paths to the CRM-wrapped version so the sidebar stays visible.
const AdminRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/crm/bookings${search}${hash}`} replace />;
};

// AppRouter handles the session_id detection for Google OAuth
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  // This prevents race conditions with AuthContext
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicLanding />} />
      <Route path="/book" element={<PartyBuilder />} />
      <Route path="/book/build" element={<PartyBuilder />} />
      <Route path="/book/legacy" element={<BookingFlow />} />
      <Route path="/booking/:bookingId/allergy-form" element={<AllergyForm />} />
      <Route path="/booking/:bookingId/payment" element={<Payment />} />
      <Route path="/booking/:bookingId/success" element={<BookingSuccess />} />
      <Route path="/booking/:bookingId/held" element={<BookingHeld />} />
      <Route path="/waiver" element={<WaiverForm />} />
      <Route path="/booking/success" element={<BookingSuccess />} />
      <Route path="/invoice/:bookingId" element={<InvoiceView />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/unique-experiences/:slug" element={<ExperienceDetailTemplate />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/my-loyalty" element={<MyLoyalty />} />
      <Route path="/my-invites" element={<MyInvites />} />
      <Route path="/my-invites/new" element={<InviteCreate />} />
      <Route path="/my-invites/:id" element={<InviteDetail />} />
      <Route path="/my-gift-cards" element={<MyGiftCards />} />
      <Route path="/my-messages" element={<MyMessages />} />
      <Route path="/gift-cards" element={<GiftCardPurchase />} />
      <Route path="/gift-cards/success" element={<GiftCardSuccess />} />
      <Route path="/rsvp/:token" element={<Rsvp />} />
      <Route path="/rsvp/:token/allergy" element={<Rsvp />} />
      <Route path="/loyalty-program" element={<LoyaltyInfo />} />
      <Route path="/birthday-invites" element={<InvitesInfo />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/admin" element={<AdminRedirect />} />
      <Route path="/admin/runsheet/:bookingId" element={<AdminRunSheet />} />
      <Route path="/my-bookings" element={<MyBookings />} />
      <Route path="/employee" element={<EmployeeDashboard />} />
      {/* CRM Routes */}
      <Route path="/crm" element={<CrmLayout />}>
        <Route index element={<CrmOverview />} />
        <Route path="bookings" element={<AdminDashboard />} />
        <Route path="bookings/:bookingId" element={<CrmBookingDetail />} />
        <Route path="customers" element={<CrmCustomers />} />
        <Route path="invoices" element={<CrmInvoices />} />
        <Route path="payments" element={<CrmPayments />} />
        <Route path="communications" element={<CrmCommunications />} />
        <Route path="emails" element={<CrmEmails />} />
        <Route path="analytics" element={<CrmAnalytics />} />
        <Route path="discounts" element={<CrmDiscounts />} />
        <Route path="hero-testimonials" element={<CrmHeroTestimonials />} />
        <Route path="loyalty" element={<CrmLoyalty />} />
        <Route path="invitations" element={<CrmInvitations />} />
        <Route path="invitations/:id" element={<CrmInvitationDetail />} />
        <Route path="gift-cards" element={<CrmGiftCards />} />
        <Route path="redeem" element={<PosRedeem />} />
        <Route path="employees" element={<CrmEmployees />} />
        <Route path="employees/:userId" element={<CrmEmployeeDetail />} />
        <Route path="staff/performance" element={<CrmStaffPerformance />} />
        <Route path="shifts" element={<CrmShifts />} />
        <Route path="shift-swaps" element={<CrmShiftSwaps />} />
        <Route path="reactivation-requests" element={<CrmReactivationRequests />} />
        <Route path="waivers" element={<CrmWaivers />} />
        <Route path="settings/employer" element={<CrmEmployerSettings />} />
        <Route path="support" element={<CrmSupport />} />
        <Route path="messages" element={<CrmMessages />} />
        <Route path="customer-messages" element={<CrmCustomerMessages />} />
        <Route path="guide" element={<CrmGuide />} />
        <Route path="reminders" element={<CrmReminders />} />
        <Route path="prospects" element={<CrmProspects />} />
        <Route path="job-applications" element={<CrmJobApplications />} />
        <Route path="audit" element={<CrmAudit />} />
        <Route path="inventory" element={<CrmInventory />} />
        <Route path="accounting" element={<CrmAccounting />} />
        <Route path="accounting/bank-imports" element={<CrmBankImports />} />
        <Route path="accounting/bank-imports/:importId" element={<CrmBankImportClassify />} />
        <Route path="payroll" element={<CrmPayroll />} />
        <Route path="products/addons" element={<CrmAddons />} />
        <Route path="settings" element={<CrmSettings />} />
        <Route path="settings/packages" element={<CrmPackages />} />
        <Route path="settings/time-slots" element={<CrmTimeSlots />} />
        <Route path="settings/special-offers" element={<CrmSpecialOffers />} />
        <Route path="settings/landing" element={<CrmCmsLanding />} />
        <Route path="settings/designs" element={<CrmLandingDesigns />} />
        <Route path="settings/menu" element={<CrmMenuManager />} />
        <Route path="settings/contact" element={<CrmContactSettings />} />
        <Route path="admin" element={<CrmAdminControl />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <IdleLogoutGate />
          <AppRouter />
          <ForceCredentialChange />
          <ChatWidget />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Mounts the idle-logout hook inside the auth/router context and renders the
// session-continuation warning modal.
function IdleLogoutGate() {
  const { user, logout } = useAuth();
  const { warningOpen, secondsLeft, countdownTotal, continueSession, logoutNow } = useIdleLogout({ user, logout });
  return (
    <IdleWarningModal
      open={warningOpen}
      secondsLeft={secondsLeft}
      total={countdownTotal}
      onContinue={continueSession}
      onLogout={logoutNow}
    />
  );
}

export default App;
